import { Component } from '@angular/core';
import { ToastService } from '@teorema/patron';
import { FindOptions, Session } from 'src/app/models';
import { MySessionFilters, SessionService } from 'src/app/shared/services';
import { AuthService } from '../../auth.service';

/** Mesma janela de `Session::ACTIVE_MINUTES` no back. */
const ACTIVE_MINUTES = 30;

/**
 * Chamado 0000577675 — sessões do próprio usuário no grupo empresa atual, na aba do perfil.
 * Segue a aba "Sessões" do detalhe do grupo empresa (company-group-sessions): pagina, busca e
 * filtra no servidor, porque sessão abandonada não é limpa e a lista cresce.
 */
@Component({
  selector: 'app-my-sessions',
  templateUrl: './my-sessions.component.html',
  styleUrls: ['../../../modules/manager/styles/detail-grid.scss'],
  standalone: false,
})
export class MySessionsComponent {
  initialized = false;
  loading = false;
  refreshing = false;
  sessions: Session[] = [];
  totalItems = 0;
  lastUpdatedAt?: Date;

  /**
   * Offset exibido pelo paginador, passado pelo `[first]` do patron-paginator. Recuar a página só
   * pelo FindOptions (ou escrevendo no p-paginator interno) deixava o paginador marcando a página
   * antiga sobre os dados da nova — ver `goToPage`.
   */
  paginatorFirst = 0;

  /** Assinatura da consulta em voo, para descartar a carga duplicada do paginador. */
  private requestKey = '';

  findOptions: FindOptions = {
    q: '',
    page: 1,
    perPage: 10,
    fields: [
      { label: 'Dispositivo' },
      { label: 'IP' },
      { label: 'Sistema' },
      { label: 'Assento' },
      { label: 'Situação' },
      { label: 'Início' },
      { label: 'Último acesso' },
      { label: 'Ações' },
    ],
  };

  filters = { only_active: false, only_linked: false };

  confirmVisible = false;
  confirmTarget?: Session;
  confirmLoading = false;

  constructor(
    private sessionService: SessionService,
    private authService: AuthService,
    private toastService: ToastService
  ) {}

  /** Chamado pelo perfil ao abrir a aba, para não ir à rede antes de a aba ser vista. */
  async init() {
    if (this.initialized) return;
    this.initialized = true;
    await this.load();
  }

  async reloadNow() {
    if (this.refreshing) return;
    this.refreshing = true;
    try {
      await this.load(true);
    } finally {
      this.refreshing = false;
    }
  }

  /** `silent`: recarga pelo botão, sem trocar a tabela pelo spinner. */
  async load(silent = false) {
    const filtros = this.buildFilters();
    const chave = JSON.stringify(filtros);

    // O patron-paginator dispara uma carga própria no ngOnInit, idêntica à do init().
    if (this.loading && this.requestKey === chave) return;

    this.requestKey = chave;
    if (!silent) this.loading = true;

    try {
      const response = await this.sessionService.mine(filtros);

      this.sessions = response?.data ?? [];
      this.totalItems = response?.total ?? this.sessions.length;
      this.lastUpdatedAt = new Date();

      // Encerrar a última sessão de uma página apaga a própria página: recua uma.
      const pagina = this.findOptions.page ?? 1;
      if (!this.sessions.length && this.totalItems > 0 && pagina > 1) {
        this.goToPage(pagina - 1);
      }
    } catch {
      this.toastService.show({ color: 'error', message: 'Houve um erro ao carregar as sessões' });
    } finally {
      if (!silent) this.loading = false;
    }
  }

  private buildFilters(): MySessionFilters {
    return {
      page: this.findOptions.page,
      size: this.findOptions.perPage,
      q: this.findOptions.q || undefined,
      only_active: this.filters.only_active || undefined,
      only_linked: this.filters.only_linked || undefined,
    };
  }

  paginate(options: FindOptions) {
    this.findOptions = { ...this.findOptions, ...options };
    this.syncPaginatorFirst();

    // O patron-tabview monta todas as abas de uma vez, e o paginador dispara esta carga no próprio
    // ngOnInit: sem a guarda, abrir "Meu perfil" já buscaria as sessões. A primeira carga é do init().
    if (!this.initialized) return;

    this.load();
  }

  /**
   * A posição visual vai pelo input `[first]`, e não por `paginator.component.first = ...`: o
   * setter do p-paginator só grava o valor, sem recalcular links nem estado, e o `first` do
   * wrapper (que alimenta o p-paginator) continuava no offset antigo.
   */
  private goToPage(page: number) {
    this.findOptions.page = page;
    this.syncPaginatorFirst();
    this.load();
  }

  private syncPaginatorFirst() {
    this.paginatorFirst = ((this.findOptions.page ?? 1) - 1) * (this.findOptions.perPage ?? 10);
  }

  onSearch(term: string) {
    this.findOptions.q = term;
    this.goToPage(1);
  }

  applyFilters() {
    this.goToPage(1);
  }

  clearFilters() {
    this.filters = { only_active: false, only_linked: false };
    this.findOptions.q = '';
    this.goToPage(1);
  }

  dash(value: any): string {
    return value === null || value === undefined || value === '' ? '—' : String(value);
  }

  sessionDevice(session: Session): string {
    return session.device_name || session.ip_address || 'Dispositivo não identificado';
  }

  seatLabel(session: Session): string {
    if (!session.web_license) return '—';

    const sistema = session.web_license.vendor_system?.name;
    return sistema
      ? `${session.web_license.number} (${sistema})`
      : String(session.web_license.number);
  }

  isInactive(session: Session): boolean {
    if (!session.last_request_at) return true;

    const limit = Date.now() - ACTIVE_MINUTES * 60 * 1000;
    return new Date(session.last_request_at).getTime() < limit;
  }

  askEndSession(session: Session) {
    this.confirmTarget = session;
    this.confirmVisible = true;
  }

  async runEndSession() {
    const target = this.confirmTarget;
    if (!target?.id) return;

    // A atual sai pelo logout normal: apagar pela rota nova faria a própria aba tomar o 401 de
    // sessão encerrada no meio do caminho.
    if (target.is_current) {
      this.confirmVisible = false;
      await this.authService.logout();
      return;
    }

    this.confirmLoading = true;

    try {
      await this.sessionService.deleteMine(target.id);

      this.toastService.show({
        color: 'success',
        message: `Sessão encerrada: ${this.sessionDevice(target)}`,
      });

      this.confirmVisible = false;
      await this.load(true);
    } catch (error: any) {
      this.toastService.show({
        color: 'error',
        message: error?.error?.message ?? 'Não foi possível encerrar a sessão',
      });
    } finally {
      this.confirmLoading = false;
    }
  }
}
